import type { CategoryDiff, HistoryIssue, SnapshotDiff } from './types';

/**
 * Basit diff: issue kodu + konum yakınlığı.
 *
 * Aynı kod ve yakın konum (aynı sayfa; satır/karakter farkı küçük) → devam eden.
 * Eşleşmeyen v1 bulguları düzeltilmiş, eşleşmeyen v2 bulguları yeni sayılır.
 */

const MAX_DISTANCE = 80;

function distance(a: HistoryIssue, b: HistoryIssue): number {
	if (a.page !== undefined && b.page !== undefined && a.page !== b.page) return Number.POSITIVE_INFINITY;
	if (a.line !== undefined && b.line !== undefined && a.line === b.line) {
		return Math.abs((a.charStart ?? 0) - (b.charStart ?? 0));
	}
	if (a.charStart !== undefined && b.charStart !== undefined) {
		return Math.abs(a.charStart - b.charStart);
	}
	if (a.line !== undefined && b.line !== undefined) {
		return Math.abs(a.line - b.line) * 20;
	}
	return 0;
}

export function diffSnapshots(v1: SnapshotDiff['v1'], v2: SnapshotDiff['v2']): SnapshotDiff {
	const fixed: HistoryIssue[] = [];
	const added: HistoryIssue[] = [];
	const ongoing: HistoryIssue[] = [];

	const codes = new Set([...v1.issues.map((i) => i.code), ...v2.issues.map((i) => i.code)]);
	for (const code of codes) {
		const left = v1.issues.filter((i) => i.code === code);
		const right = v2.issues.filter((i) => i.code === code);
		const used = new Set<number>();

		for (const a of left) {
			let best = -1;
			let bestDistance = Number.POSITIVE_INFINITY;
			for (let i = 0; i < right.length; i++) {
				if (used.has(i)) continue;
				const d = distance(a, right[i]);
				if (d < bestDistance) {
					bestDistance = d;
					best = i;
				}
			}
			if (best >= 0 && bestDistance <= MAX_DISTANCE) {
				used.add(best);
				ongoing.push(a);
			} else {
				fixed.push(a);
			}
		}

		for (let i = 0; i < right.length; i++) {
			if (!used.has(i)) added.push(right[i]);
		}
	}

	const categoryKeys = new Set([
		...v1.categories.map((c) => c.category),
		...v2.categories.map((c) => c.category)
	]);
	const categories: CategoryDiff[] = [...categoryKeys].map((category) => {
		const a = v1.categories.find((c) => c.category === category)?.score;
		const b = v2.categories.find((c) => c.category === category)?.score;
		return {
			category,
			v1: a,
			v2: b,
			delta: a !== undefined && b !== undefined ? b - a : undefined
		};
	});

	return {
		v1,
		v2,
		scoreDelta: v2.score - v1.score,
		gradeChanged: v1.grade !== v2.grade,
		categories,
		fixed,
		added,
		ongoing
	};
}
